<template>
  <v-layout row justify-center>
    <v-flex xs6>
      <v-dialog v-model="dialog" max-width="800px">
        <v-card>
          <v-card-title class="headline font-weight-light" v-if="selectedOffer.assetReference !== null">
            {{ messages.title.EDIT_OFFER }} {{ selectedOffer.title }}
          </v-card-title>
          <v-card-title class="headline font-weight-light" v-else>
            {{ messages.title.LINK_OFFER }} {{ selectedOffer.title }}
          </v-card-title>
          <v-card-text class="pt-1">
            <v-alert color="success" icon="check_circle" value="true" v-if="isEditSuccess && editResponseMsg !== ''">
              <span v-html="editResponseMsg"></span>
            </v-alert>
            <v-alert color="error" icon="warning" value="true" v-if="!isEditSuccess && editResponseMsg !== ''">
              <span v-html="editResponseMsg"></span>
            </v-alert>
            <v-layout row wrap class="pt-2">
              <v-flex xs6>
                <v-form ref="form" v-model="formValid" lazy-validation class="pr-3">
                  <v-text-field v-model="assetReference" :rules="assetRefRules" label="Asset Reference"
                    hint="A valid PAID value can be used" class="mb-3" persistent-hint required>
                  </v-text-field>
                  <v-card-title class="px-0">
                    <label class="subheading">Alternate Ids</label>
                    <v-spacer></v-spacer>
                    <v-btn small color="primary" flat :disabled="currentComponent === 'AlternateIdForm'"
                      @click="showAltIdform()"> Add
                      <v-icon right>add_circle</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-data-table :headers="headers" :items="alternateIds" hide-actions>
                    <template slot="items" slot-scope="props">
                      <tr @click="showAltIdform(props.item)">
                        <td class="text-xs-left">{{ props.item.key }}</td>
                        <td class="text-xs-left">{{ props.item.value }}</td>
                        <td class="justify-center layout px-0">
                          <v-btn icon class="mx-0" @click.stop="deleteAltIdConfirmation(props.item)">
                            <v-icon color="pink">delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                    <template slot="no-data">
                      <v-alert :value="true" color="info black--text" icon="warning">
                        No alternate Ids defined
                      </v-alert>
                    </template>
                  </v-data-table>
                </v-form>
              </v-flex>
              <v-flex xs6>
                <!-- Dynamic component -->
                <transition name="slide-x-reverse-transition" mode="out-in">
                  <component v-bind:is="currentComponent" v-bind="currentProperties"
                    @event:CloseAltIdForm="closeAltIdForm" @event:EditAltId="editAltId" @event:AddAltId="addAltId">
                  </component>
                </transition>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" v-if="isEditSuccess" @click.native="doneEditing()">{{ messages.label.DONE }}</v-btn>
            <div v-else>
              <v-btn color="grey lighten-1 white--text" @click.native="cancelEditing()">{{ messages.label.CANCEL }}
              </v-btn>
              <v-btn color="primary" :disabled="!isOfferModified" :loading="editInProgress" @click.native="editOffer()">
                {{ messages.label.SAVE }}</v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
    <v-layout row justify-center>
      <v-dialog v-model="deleteAlert" persistent max-width="290">
        <v-card>
          <v-card-title class="headline">Delete Alternate Id?</v-card-title>
          <v-card-text>Are you sure you want to delete Alternate Id
            <b>'{{ altIdToDelete.key }}: {{ altIdToDelete.value }}'</b> ?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary darken-1" flat @click.native="deleteAlert = false">Cancel</v-btn>
            <v-btn color="error darken-1" @click.native="deleteAltId">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-layout>
</template>

<script>
import { mapGetters } from "vuex"
import OfferDetails from "@/PreIngest/Offers/OfferDetails"
import AlternateIdForm from "@/PreIngest/Offers/AlternateIdForm"
import PreIngestService from "@/PreIngest/PreIngestService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import AppService from "@/AppService"
import {
  VOD_SYCN_OFFERING_DATA_SERVICE_URL
} from "@/store/constants/getter-types"
import messages from "@/PreIngest/messages.json"

export default {
  name: "OffersEdit",
  components: {
    OfferDetails,
    AlternateIdForm
  },
  data() {
    return {
      dialog: false,
      deleteAlert: false,
      isEditSuccess: false,
      assetReference: null,
      editResponseMsg: "",
      editInProgress: false,
      selectedOffer: {},
      altIdToDelete: {},
      assetRefRules: [
        (v) => !!v || "Asset Reference is required",
        this.isValidPAID
      ],
      formValid: false,
      headers: this.getTableHeaders(),
      alternateIds: [],
      originalalternateIds: [],
      currentComponent: "OfferDetails",
      currentProperties: {
        selectedOffer: {}
      }
    }
  },
  computed: {
    ...mapGetters([
      VOD_SYCN_OFFERING_DATA_SERVICE_URL
    ]),
    isOfferModified() {
      return (
        this.assetReference !== this.selectedOffer.assetReference ||
        !this.$lodash.isEqual(this.alternateIds, this.originalalternateIds)
      )
    }
  },
  methods: {
    /**
     * Validate if given value is a valid PAID
     * AssetID format : 4 Chars followed by 16 Numerals
     */
    isValidPAID(value) {
      let assetIdRegEx = new RegExp("^[a-zA-Z]{4}[0-9]{16}$")
      let isValid = assetIdRegEx.test(value)
      return isValid || "Enter a valid PAID, ex: ABAB1111222233334444"
    },

    /**
     * doneEditing - reload offers
     *
     * @return {type}  description
     */
    doneEditing() {
      this.dialog = false
      let refreshReason = {
        edit: true,
        editedRecordId: this.selectedOffer.id
      }
      PreIngestService.$emit("event:refreshOffers", refreshReason)
      this.resetData()
    },

    /**
     * cancelEditing - discontinue editing offer details
     */
    cancelEditing() {
      this.dialog = false
      this.resetData()
    },

    /**
     * resetData - reset data for this dialog
     */
    resetData() {
      this.isEditSuccess = false
      this.editResponseMsg = ""
      this.editInProgress = false
      this.selectedOffer = {}
      this.originalalternateIds = []
      this.assetReference = null
      this.$refs.form.reset()
      this.currentComponent = "OfferDetails"
    },

    /**
     * editOffer - edit offer details and communicate to service
     */
    editOffer() {
      if (this.$refs.form.validate()) {
        this.editInProgress = true
        this.selectedOffer.alternateIds = {}
        this.selectedOffer.assetReference = this.assetReference
        this.alternateIds.forEach((altId) => {
          this.selectedOffer.alternateIds[altId.key] = altId.value
        })
        let reqBody = {
          id: this.selectedOffer.id,
          guid: this.selectedOffer.guid,
          assetReference: this.selectedOffer.assetReference,
          alternateIds: this.selectedOffer.alternateIds
        }
        preIngestDataService.updateOffer(reqBody,
          this.VOD_SYCN_OFFERING_DATA_SERVICE_URL)
          .then(() => {
            this.editInProgress = false
            this.isEditSuccess = true
            //  let publishMetadataResponse = response.data.publishMetadataResponse
            this.editResponseMsg = this.messages.info.OFFER_EDIT_SUCCESS
          })
          .catch(() => {
            this.editInProgress = false
            this.isEditSuccess = false
            //  let response = error.response.data
            this.editResponseMsg = this.messages.error.OFFER_EDIT_FAILURE
          })
      }
    },

    /**
     * getTableHeaders - Returns table headers for alternate ids table
     *
     * @return {Array}  Array of table headers
     */
    getTableHeaders() {
      return [
        {
          text: "Key",
          align: "left",
          value: "",
          sortable: false
        },
        {
          text: "Value",
          align: "left",
          value: "",
          sortable: false
        },
        {
          text: "",
          sortable: false
        }
      ]
    },

    /**
     * initAlternateIds - Alternate ids are in object format, convert them to
     * array of object to enable easy editing
     */
    initAlternateIds() {
      this.alternateIds = []
      // Map alternate ids
      let index = 1
      this.$lodash.forOwn(this.selectedOffer.alternateIds, (value, key) => {
        this.alternateIds.push({
          key: key,
          value: value,
          id: index
        })
        index++
      })
      this.originalalternateIds = this.$lodash.clone(this.alternateIds) // For detecting change
    },

    /**
     * showAltIdform - shows alternate id edit / add form
     */
    showAltIdform(altId) {
      if (!this.$lodash.isEmpty(altId)) {
        let selectedAltId = this.$lodash.clone(altId)
        this.currentProperties.altId = selectedAltId
      } else {
        this.currentProperties.altId = {}
      }
      if (this.currentComponent === "AlternateIdForm") {
        this.currentComponent = false
        this.$nextTick(() => {
          this.currentComponent = "AlternateIdForm"
        })
      } else {
        this.currentComponent = false
        this.currentComponent = "AlternateIdForm"
      }
    },

    /**
     * closeAltIdForm - Close edit / add alternate id form and display offer
     * details
     */
    closeAltIdForm() {
      this.currentProperties.selectedOffer = this.selectedOffer
      if (this.currentComponent === "OfferDetails") {
        this.currentComponent = false
        this.$nextTick(() => {
          this.currentComponent = "OfferDetails"
        })
      } else {
        this.currentComponent = false
        this.currentComponent = "OfferDetails"
      }
    },

    /**
     * editAltId - edit details for selected alternate id
     *
     * @param  {object} altId alternate id to edit
     */
    editAltId(altId) {
      let editIndex = this.$lodash.findIndex(this.alternateIds, (altIdObj) => {
        return altIdObj.id === altId.id
      })
      if (editIndex !== -1) {
        this.alternateIds[editIndex].key = altId.key
        this.alternateIds[editIndex].value = altId.value
      }
      this.closeAltIdForm()
    },

    /**
     * addAltId - adds new alternate id
     *
     * @param  {object} altId new alternate id object
     */
    addAltId(altId) {
      this.closeAltIdForm()
      // add Id
      altId.id = this.alternateIds.length + 1
      this.alternateIds.push(altId)
    },

    /**
     * deleteAltIdConfirmation - description
     *
     * @param  {object} altId selected alternate id to delete
     */
    deleteAltIdConfirmation(altId) {
      this.altIdToDelete = altId
      this.deleteAlert = true
    },

    /**
     * deleteAltId - Deletes selected alternate id
     */
    deleteAltId() {
      this.deleteAlert = false
      let deleteIndex = this.$lodash.findIndex(
        this.alternateIds,
        (altIdObj) => {
          return altIdObj.id === this.altIdToDelete.id
        }
      )
      if (deleteIndex !== -1) {
        this.alternateIds.splice(deleteIndex, 1)
      }
      this.closeAltIdForm()
    },

    /**
     * initialize - Initializes data for editing offer details
     */
    initialize(offer) {
      console.log("[OffersEdit:initialize] offer", offer)
      this.resetData()
      this.dialog = true
      this.selectedOffer = offer
      this.assetReference = this.selectedOffer.assetReference
      // Pass properties to dynamic component
      this.currentProperties.selectedOffer = this.selectedOffer
      this.initAlternateIds()
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages

    /**
     * Events
     */
    AppService.$on("event:EditOffer", this.initialize)
  },
  beforeDestroy() {
    AppService.$off("event:EditOffer", this.initialize)
  }
}
</script>

<style lang="css">
</style>



// WEBPACK FOOTER //
// src/PreIngest/Offers/OffersEdit.vue